import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatRelativeDate } from '../utils';

const RecentSearches = ({ recentSearches = [], isLoading }) => {
  const navigate = useNavigate();
  const list = Array.isArray(recentSearches) ? recentSearches : [];

  const executeSearch = (search) => {
    const params = new URLSearchParams();
    const location = search.location || search.city || search.query;
    const type = search.propertyType || search.property_type || search.type;
    const minPrice = search.minPrice || search.min_price;
    const maxPrice = search.maxPrice || search.max_price;
    const bedrooms = search.bedrooms;

    if (location) params.set('location', location);
    if (type) params.set('type', type);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (bedrooms) params.set('bedrooms', bedrooms);

    navigate(`/property-search-browse?${params.toString()}`);
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Recent searches</h2>
          <p className="text-xs text-slate-500 mt-0.5">Repeat a past search in one click</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/property-search-browse')}
          iconName="Plus"
          iconPosition="left"
        >
          New search
        </Button>
      </div>

      {isLoading ? (
        <div className="p-5 space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-14 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-10 px-5">
          <Icon name="Search" size={28} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-600 mb-3">No recent searches</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/property-search-browse')}
            iconName="Search"
            iconPosition="left"
          >
            Start searching
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.slice(0, 5).map((search) => {
            const label =
              search.location ||
              search.city ||
              search.query ||
              search.keywords ||
              'Property search';
            return (
              <li key={search.id || label}>
                <button
                  type="button"
                  onClick={() => executeSearch(search)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-slate-50/80 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={14} className="text-slate-400 flex-shrink-0" />
                      <span className="text-sm font-semibold text-slate-900 truncate">{label}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 truncate pl-5">
                      {[
                        search.propertyType || search.property_type,
                        search.priceRange ||
                          (search.min_price || search.max_price
                            ? `${search.min_price || '…'} – ${search.max_price || '…'}`
                            : null),
                        search.bedrooms ? `${search.bedrooms}+ beds` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Open filters'}
                      {search.search_date || search.searchDate || search.created_at
                        ? ` · ${formatRelativeDate(
                            search.search_date || search.searchDate || search.created_at
                          )}`
                        : ''}
                    </p>
                  </div>
                  <Icon name="RotateCcw" size={16} className="text-slate-400 flex-shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default RecentSearches;
